import { useEffect, useRef } from "react";
import { gsap } from "@/animations/gsap";
import { useIsDesktop } from "@/hooks/useDevice";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

export function Cursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();
  const reduced = usePrefersReducedMotion();
  const active = isDesktop && !reduced;

  useEffect(() => {
    if (!active) return undefined;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return undefined;

    document.documentElement.classList.add("has-custom-cursor");
    gsap.set([dot, ring], { opacity: 0 });

    const dotX = gsap.quickTo(dot, "x", { duration: 0.12, ease: "power3" });
    const dotY = gsap.quickTo(dot, "y", { duration: 0.12, ease: "power3" });
    const ringX = gsap.quickTo(ring, "x", { duration: 0.45, ease: "power3" });
    const ringY = gsap.quickTo(ring, "y", { duration: 0.45, ease: "power3" });

    const move = (e: MouseEvent) => {
      dotX(e.clientX);
      dotY(e.clientY);
      ringX(e.clientX);
      ringY(e.clientY);
      gsap.to([dot, ring], { opacity: 1, duration: 0.25, overwrite: "auto" });
    };
    const over = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest("a, button, [data-cursor-hover]");
      gsap.to(ring, { scale: target ? 1.7 : 1, duration: 0.35, overwrite: "auto" });
      gsap.to(dot, { scale: target ? 0.45 : 1, duration: 0.35, overwrite: "auto" });
    };
    const leave = () => gsap.to([dot, ring], { opacity: 0, duration: 0.2, overwrite: "auto" });

    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    document.documentElement.addEventListener("mouseleave", leave);

    return () => {
      document.documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      document.documentElement.removeEventListener("mouseleave", leave);
    };
  }, [active]);

  if (!active) return null;

  return (
    <>
      <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="cursor-ring" aria-hidden="true" />
    </>
  );
}
